import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TITLES_DEFAULT_PARAMS,
  queryOptionsGetTitlesPaginated,
} from '../../../../api/titles/titlesApi';
import {
  AppPagination,
  AppPaginationFooter,
  AppPaginationRange,
} from '../../../../components/AppPagination';
import { AppSearchInput } from '../../../../components/AppSearchInput';
import { EntityList } from '../../../../components/EntityList';
import { MSR3Empty } from '../../../../components/MSR3Empty';
import { SingleQueryResults } from '../../../../components/QueryResults/SingleQueryResults';
import {
  TitleCard,
  TitleCardSkeleton,
} from '../../../../components/TitleCard/TitleCard';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { useDebouncedUrlParam } from '../../../../hooks/useDebouncedUrlParam';
import { usePaginationBounds } from '../../../../hooks/usePaginationBounds';
import { useResetPageOnSearchChange } from '../../../../hooks/useResetPageOnSearchChange';
import { useSetSearchParam } from '../../../../hooks/useSetSearchParam';
import { cappedPagination } from '../../../../utils/helpers/cappedTotal';

const PAGE_PARAM = 'clientTitlesPage';
const SEARCH_PARAM = 'clientTitlesSearch';
const PAGE_SIZE = TITLES_DEFAULT_PARAMS.pageSize;

interface ClientTitlesSectionProps {
  clientId: string;
}

/**
 * MSR3 Clients Summary "Client Titles" card (S2). Own URL params
 * (`clientTitlesPage`/`clientTitlesSearch`) so paginating here never bleeds
 * into the Statements section's page state — the same reason
 * `StatementsDataTable` takes a caller-owned `paginationKey`.
 *
 * `TitleCard` always renders with an empty `clientNames` — the page is
 * already scoped to this one client, so repeating its name on every row
 * would be redundant, the same reasoning behind hiding the Client column on
 * the Statements table (ADR-CLP-002).
 */
export const ClientTitlesSection = ({ clientId }: ClientTitlesSectionProps) => {
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;
  const basePath = `/workspace/${workspaceId ?? ''}`;

  const [searchParams] = useSearchParams();
  const setParam = useSetSearchParam();

  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const page = Math.max(1, Number(searchParams.get(PAGE_PARAM)) || 1);

  const setSearch = useCallback(
    (value: string) => setParam(SEARCH_PARAM, value),
    [setParam],
  );
  const setPage = useCallback(
    (next: number) => setParam(PAGE_PARAM, String(next)),
    [setParam],
  );

  useResetPageOnSearchChange(search, PAGE_PARAM, setPage);

  const titlesQuery = useQuery({
    ...queryOptionsGetTitlesPaginated({
      name: search || undefined,
      ownerClientIds: [clientId],
      page,
      pageSize: PAGE_SIZE,
      // Series level only — the backend excludes child episodes, matching
      // the Titles L1 list's own convention.
      seriesOnly: true,
      workspaceId,
    }),
    enabled: Boolean(clientId && workspaceId),
  });

  const {
    input: searchInput,
    isSearchFetching = false,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(search, setSearch, {
    queryStatus: {
      isFetching: titlesQuery.isFetching,
      isPending: titlesQuery.isPending,
    },
  });

  // The titles list caps its count (APP-2589); a capped total is a floor.
  const { isTotalCapped, itemCount, totalItems, totalPages } = cappedPagination(
    titlesQuery.data?.meta,
  );

  const { clampedPage } = usePaginationBounds({
    isSuccess: titlesQuery.isSuccess,
    page,
    setPage,
    totalItems,
    totalPages,
  });

  const hasSearch = Boolean(search);
  const areControlsDisabled = titlesQuery.isPending;

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>Client Titles</EntityList.Title>
        <EntityList.Count count={totalItems} isCapped={isTotalCapped} />
      </EntityList.Header>

      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <AppSearchInput
          classes={{ wrapper: 'sm:w-64 md:w-96' }}
          disabled={areControlsDisabled}
          isFetching={isSearchFetching}
          placeholder="Search titles"
          value={searchInput}
          onChange={setSearchInput}
        />

        <div className="ml-auto flex items-center gap-2">
          <AppPaginationRange
            currentPage={clampedPage}
            isTotalCapped={isTotalCapped}
            itemCount={itemCount}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
          />

          <AppPagination
            currentPage={clampedPage}
            disabled={areControlsDisabled}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </div>

      <SingleQueryResults
        loadingFallback={
          <EntityList.Content>
            {Array.from({ length: 3 }).map((_, index) => (
              <TitleCardSkeleton key={index} />
            ))}
          </EntityList.Content>
        }
        query={titlesQuery}
      >
        {({ items: titles }) =>
          titles.length === 0 ? (
            <MSR3Empty
              {...MSR3Empty.Props[hasSearch ? 'no-result' : 'title']}
            />
          ) : (
            <EntityList.Content>
              {titles.map((title) => (
                <TitleCard
                  key={title._id}
                  title={title}
                  basePath={basePath}
                  clientNames={[]}
                />
              ))}
            </EntityList.Content>
          )
        }
      </SingleQueryResults>

      <AppPaginationFooter
        currentPage={clampedPage}
        disabled={areControlsDisabled}
        isTotalCapped={isTotalCapped}
        itemCount={itemCount}
        pageSize={PAGE_SIZE}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </EntityList>
  );
};
